import { useState, useEffect, useCallback, useRef } from 'react'

export interface StandingTeam {
  rank: number
  team: {
    id: number
    name: string
    logo: string
  }
  points: number
  played: number
  win: number
  draw: number
  lose: number
  goalsFor: number
  goalsAgainst: number
  goalDifference: number
  form?: string[]
}

export interface LeagueStanding {
  id: number
  name: string
  logo: string
  priority: number
  season: string
  teams: StandingTeam[]
}

export interface UseStandingsProps {
  leagueId?: number
  autoRefresh?: boolean
  refreshInterval?: number
}

export interface UseStandingsReturn {
  standings: LeagueStanding | null
  loading: boolean
  error: string | null
  refetch: () => void
  performance?: {
    fetchTime?: number
    cached?: boolean
  }
}

// Browser cache for standings data
const cache = new Map<string, { data: LeagueStanding; timestamp: number; performance: any }>()
const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes client-side cache

export function useStandings({
  leagueId = 39, // Default to Premier League
  autoRefresh = false,
  refreshInterval = 300000 // 5 minutes
}: UseStandingsProps = {}): UseStandingsReturn {
  const [standings, setStandings] = useState<LeagueStanding | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [performance, setPerformance] = useState<{ fetchTime?: number; cached?: boolean }>({})
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchStandings = useCallback(async (useStaleWhileRevalidate = false) => {
    const startTime = Date.now()
    const cacheKey = `standings_${leagueId}`
    
    // Check browser cache first
    const cached = cache.get(cacheKey)
    const now = Date.now()
    
    if (cached && now - cached.timestamp < CACHE_DURATION) {
      // Use cached data immediately
      setStandings(cached.data)
      setPerformance({ 
        fetchTime: Date.now() - startTime, 
        cached: true 
      })
      
      if (!useStaleWhileRevalidate) {
        return
      }
    }

    // Cancel previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()
    
    if (!useStaleWhileRevalidate) {
      setLoading(true)
    }
    setError(null)

    let retryCount = 0
    const maxRetries = 2

    while (retryCount <= maxRetries) {
      try {
        const response = await fetch(`/api/football/standings?league=${leagueId}`, {
          signal: abortControllerRef.current.signal,
          headers: {
            'Cache-Control': 'no-cache',
          }
        })
        
        if (!response.ok) {
          // Don't retry on 400 errors (bad request)
          if (response.status >= 400 && response.status < 500) {
            throw new Error(`Invalid request: ${response.status}`)
          }
          // Retry on 500+ errors
          if (retryCount < maxRetries) {
            retryCount++
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)) // Exponential backoff
            continue
          }
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        if (!result.success) {
          // Don't retry on logical errors
          throw new Error(result.error || 'Failed to fetch standings')
        }

        const fetchTime = Date.now() - startTime
        
        // Update state
        setStandings(result.data)
        setPerformance({ 
          fetchTime, 
          cached: result.cached || false 
        })
        
        // Update browser cache
        cache.set(cacheKey, {
          data: result.data,
          timestamp: now,
          performance: result.performance
        })
        
        // Success - break out of retry loop
        break
        
      } catch (err) {
        // Don't show error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        
        // If we're on the last retry, handle the error
        if (retryCount >= maxRetries) {
          const errorMsg = err instanceof Error ? err.message : 'Có lỗi xảy ra khi tải dữ liệu'
          
          // Only show error if we don't have cached data to fall back to
          if (!cached || useStaleWhileRevalidate) {
            setError(errorMsg)
          }
          
          console.error(`[useStandings] Error for league ${leagueId} after ${retryCount + 1} attempts:`, err)
          
          // If we have cached data and error occurs, keep using cached data
          if (cached && !useStaleWhileRevalidate) {
            setStandings(cached.data)
          }
          break
        } else {
          // Not the last retry, continue to next attempt
          retryCount++
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)) // Exponential backoff
        }
      }
    }

    setLoading(false)
    abortControllerRef.current = null
  }, [leagueId])

  const refetch = useCallback(() => {
    fetchStandings(false) // Force fresh fetch
  }, [fetchStandings])

  // Initial fetch with stale-while-revalidate
  useEffect(() => {
    fetchStandings(true)
  }, [fetchStandings])

  // Auto refresh with stale-while-revalidate
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      fetchStandings(true) // Use stale-while-revalidate for auto refresh
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval, fetchStandings])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  return {
    standings,
    loading,
    error,
    refetch,
    performance
  }
} 